import React, { createContext, useContext, useReducer, ReactNode, useCallback } from "react";
import AuthClient, { AdminProfile } from "../api/AuthClient";

interface AuthState {
  user: AdminProfile | null;
  token: string | null;
  region: string | null;
  email: string | null;
  isLoading: boolean;
  isProfileLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

type AuthAction =
  | { type: "SET_TOKEN"; payload: { token: string; region: string; email: string } }
  | { type: "SET_PROFILE"; payload: AdminProfile }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_PROFILE_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_SAVING"; payload: boolean }
  | { type: "UPDATE_PROFILE_SUCCESS"; payload: Partial<AdminProfile> }
  | { type: "LOGOUT" }
  | { type: "CLEAR_ERROR" };

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem("iqadmin_token"),
  region: localStorage.getItem("iqadmin_region"),
  email: localStorage.getItem("iqadmin_email"),
  isLoading: false,
  isProfileLoading: false,
  isSaving: false,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "SET_TOKEN":
      return {
        ...state,
        token: action.payload.token,
        region: action.payload.region,
        email: action.payload.email,
        isLoading: false,
        error: null,
      };
    case "SET_PROFILE":
      return { ...state, user: action.payload, isProfileLoading: false };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_PROFILE_LOADING":
      return { ...state, isProfileLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload, isLoading: false, isSaving: false };
    case "SET_SAVING":
      return { ...state, isSaving: action.payload };
    case "UPDATE_PROFILE_SUCCESS":
      return { ...state, user: { ...state.user, ...action.payload } as AdminProfile, isSaving: false };
    case "LOGOUT":
      return { ...initialState, token: null, region: null, email: null, user: null };
    case "CLEAR_ERROR":
      return { ...state, error: null };
    default:
      return state;
  }
}

interface AuthContextType {
  state: AuthState;
  dispatch: React.Dispatch<AuthAction>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: Partial<AdminProfile>) => Promise<{success: boolean; error?: string}>;
  uploadAdminLogo: (formData: FormData | null, queryParams?: string) => Promise<{success: boolean; error?: string}>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Applies a favicon URL to the browser <head> dynamically. */
const applyFavicon = (url: string) => {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const login = async (email: string, password: string) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const res = await AuthClient.login({
        email_address: email.trim().toLowerCase(),
        entity_password: password,
        entity_type: "INTERVIEWIQ_ADMIN",
      });

      if (res.error) {
        dispatch({ type: "SET_ERROR", payload: res.error });
        return;
      }

      const tokenResponse = res.detail as any;
      const token = tokenResponse.access_token;
      const region = tokenResponse.region || "ap-south-1";
      const emailAddr = email.trim().toLowerCase();

      localStorage.setItem("iqadmin_token", token);
      localStorage.setItem("iqadmin_region", region);
      localStorage.setItem("iqadmin_email", emailAddr);

      dispatch({
        type: "SET_TOKEN",
        payload: { token, region, email: emailAddr },
      });

      if (tokenResponse.previous_session) {
        return;
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to connect to the server.";
      dispatch({ type: "SET_ERROR", payload: msg });
      throw err;
    }
  };

  const fetchProfile = useCallback(async () => {
    const token = state.token || localStorage.getItem("iqadmin_token");
    if (!token) return;

    dispatch({ type: "SET_PROFILE_LOADING", payload: true });
    try {
      const res = await AuthClient.getProfile();
      if (res.error && res.error !== null) {
        dispatch({ type: "SET_PROFILE_LOADING", payload: false });
        return;
      }
      
      const profile = (res.detail || res.data || res) as AdminProfile;
      if (profile && Object.keys(profile).length > 0) {
        dispatch({ type: "SET_PROFILE", payload: profile });
        localStorage.setItem("iqadmin_user", JSON.stringify(profile));
        // Persist and apply branding
        if (profile.logo_url) {
          localStorage.setItem("iqadmin_logo", profile.logo_url);
        } else {
          localStorage.removeItem("iqadmin_logo");
        }
        if (profile.favicon_url) {
          localStorage.setItem("iqadmin_favicon", profile.favicon_url);
          applyFavicon(profile.favicon_url);
        } else {
          localStorage.removeItem("iqadmin_favicon");
        }
      } else {
        dispatch({ type: "SET_PROFILE_LOADING", payload: false });
      }
    } catch {
      dispatch({ type: "SET_PROFILE_LOADING", payload: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.token]);

  const updateProfile = async (data: Partial<AdminProfile>) => {
    dispatch({ type: "SET_SAVING", payload: true });
    try {
      const res = await AuthClient.saveProfile(data);
      if (res.error) {
        dispatch({ type: "SET_ERROR", payload: res.error });
        return { success: false, error: res.error };
      }
      dispatch({ type: "UPDATE_PROFILE_SUCCESS", payload: data });
      if (state.user) {
        const updatedUser = { ...state.user, ...data };
        localStorage.setItem("iqadmin_user", JSON.stringify(updatedUser));
      }
      return { success: true };
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to update profile.";
      dispatch({ type: "SET_ERROR", payload: msg });
      return { success: false, error: msg };
    }
  };

  const logout = async () => {
    try {
      await AuthClient.logout();
    } catch {
      // proceed with local logout even if API fails
    }
    localStorage.removeItem("iqadmin_token");
    localStorage.removeItem("iqadmin_region");
    localStorage.removeItem("iqadmin_email");
    localStorage.removeItem("iqadmin_password");
    localStorage.removeItem("iqadmin_user");
    localStorage.removeItem("iqadmin_logo");
    localStorage.removeItem("iqadmin_favicon");
    dispatch({ type: "LOGOUT" });
  };

  const clearError = () => dispatch({ type: "CLEAR_ERROR" });

  const uploadAdminLogo = async (formData: FormData | null, queryParams?: string): Promise<{success: boolean; error?: string}> => {
    try {
      const res = await AuthClient.uploadLogo(formData, queryParams);
      if (res.error) {
        return { success: false, error: res.error };
      }
      // Refresh profile so branding URLs are updated in state
      await fetchProfile();
      return { success: true };
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to upload logo.";
      return { success: false, error: msg };
    }
  };


  return (
    <AuthContext.Provider value={{ state, dispatch, login, logout, fetchProfile, updateProfile, uploadAdminLogo, clearError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
